"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

type EntityTypeContextType = {
  entityType: string
  setEntityType: (value: string) => void
}

const DEFAULT_ENTITY_TYPE = "WorkOrder"

const EntityTypeContext = createContext<EntityTypeContextType | undefined>(undefined)

/**
 * EntityTypeProvider has two modes:
 *
 *  1. **URL-bound (default)** — no `value` prop, behaves as before: reads
 *     `?entityType=` on mount, writes it back on change, listens to popstate.
 *     Use this at the app root for the main workflow grid.
 *
 *  2. **Controlled** — pass `value="VmsRequest"` (or similar). The provider
 *     skips URL plumbing entirely and just exposes the static entity type to
 *     descendants. `setEntityType` becomes a no-op in this mode. Use this to
 *     scope a sub-tree to a specific entity type without hijacking the URL —
 *     e.g. the VMS tab's per-sub-tab WorkflowProvider needs Requests / Emails
 *     scoping that's independent of the outer URL state.
 */
export function EntityTypeProvider({ children, value }: { children: ReactNode; value?: string }) {
  const isControlled = typeof value === "string" && value.trim().length > 0
  const [entityType, setEntityTypeState] = useState<string>(value ?? DEFAULT_ENTITY_TYPE)

  // Controlled: keep state synced to the prop, skip all URL plumbing.
  useEffect(() => {
    if (isControlled) {
      setEntityTypeState(value!)
    }
  }, [isControlled, value])

  useEffect(() => {
    if (isControlled) return
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get("entityType")
    if (fromUrl && fromUrl.trim().length > 0) {
      setEntityTypeState(fromUrl)
    } else {
      // Ensure default is reflected in the URL for bookmarking/sharing
      params.set("entityType", DEFAULT_ENTITY_TYPE)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState({}, "", newUrl)
    }
  }, [isControlled])

  useEffect(() => {
    if (isControlled) return
    const onPopState = () => {
      if (typeof window === "undefined") return
      const params = new URLSearchParams(window.location.search)
      const fromUrl = params.get("entityType")
      if (fromUrl && fromUrl !== entityType) {
        setEntityTypeState(fromUrl)
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [entityType, isControlled])

  const setEntityType = (next: string) => {
    if (isControlled) return // No-op — parent owns the value via prop.
    setEntityTypeState(next)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      params.set("entityType", next)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState({}, "", newUrl)
    }
  }

  const contextValue = useMemo(() => ({ entityType, setEntityType }), [entityType])

  return (
    <EntityTypeContext.Provider value={contextValue}>{children}</EntityTypeContext.Provider>
  )
}

export function useEntityType() {
  const ctx = useContext(EntityTypeContext)
  if (!ctx) throw new Error("useEntityType must be used within an EntityTypeProvider")
  return ctx
}


